import { useState } from "react";
import { toast } from "sonner";
import { BadgePercent } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { ACCOUNT_TYPES, accountTypeOf, accountTypeLabel } from "@/lib/accountTypes";

const OWNER_ROLES = ["developer_admin", "marketing_agent"];

function effect(from, to) {
  if (to === "marketing_agent") return "A flat Marketing Agent discount will apply automatically to eligible future LaunchX360 payments (not taxes, Meta ad spend or third-party charges). You'll also be able to onboard and manage other companies.";
  if (from === "marketing_agent") return "The Marketing Agent discount will stop for future payments. Any unpaid balance will be recalculated without it. Companies you already manage stay available to you.";
  return "Your pricing does not change.";
}

export default function AccountSettings() {
  const { me, reload } = useAuth();
  const cur = accountTypeOf(me.user);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const owner = OWNER_ROLES.includes(me.user.role);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/auth/account-type", { account_type: pick });
      toast.success(`Account type changed to ${accountTypeLabel(pick)}`);
      setPick(null);
      await reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-6 max-w-2xl" data-testid="account-settings-page">
      <PageHeader label="Account" title="Account Settings" subtitle="Your profile and account type." />
      <div className="lx-card space-y-1 text-sm" data-testid="account-profile">
        <div className="font-semibold text-base">{me.user.name}</div>
        <div className="text-slate-500">{me.user.email}{me.user.phone ? ` · ${me.user.phone}` : ""}</div>
        <div className="text-slate-500">Workspace: {me.organization?.name}</div>
      </div>
      <div className="lx-card space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="font-semibold">Account type</div>
          <span className="lx-ai-badge" data-testid="account-type-current">{accountTypeLabel(cur)}</span>
        </div>
        <p className="text-xs text-slate-500">Every account type gets the same platform and plan features. Choosing Marketing Agent adds an automatic discount on eligible future payments. Completed payments, invoices and orders are never changed. Projects, subscriptions, team and payment history stay as they are.</p>
        {!owner && <p className="text-xs text-amber-600" data-testid="account-type-owner-only">Only the workspace owner can change the account type. Ask your workspace admin.</p>}
        {owner && !me.user.is_workspace_owner && cur !== "marketing_agent" && <p className="text-xs text-amber-600" data-testid="account-type-creator-only">Only the person who created this workspace can switch it to Marketing Agent.</p>}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="account-type-options">
          {ACCOUNT_TYPES.map(([k, l]) => (
            <button key={k} type="button" disabled={!owner || k === cur || (k === "marketing_agent" && !me.user.is_workspace_owner)} onClick={() => setPick(k)} data-testid={`account-type-option-${k}`}
              className={`text-sm py-2 px-2 rounded-md border transition-colors disabled:cursor-not-allowed ${k === cur ? "bg-emerald-600 border-emerald-600 text-white font-semibold" : "border-slate-800 text-slate-500 hover:bg-slate-800 disabled:opacity-50"}`}>
              {l}{k === "marketing_agent" && <BadgePercent className="inline h-3.5 w-3.5 ml-1" />}
            </button>))}
        </div>
      </div>
      <AlertDialog open={!!pick} onOpenChange={(o) => !o && setPick(null)}>
        <AlertDialogContent className="bg-white" data-testid="account-type-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Change to {pick && accountTypeLabel(pick)}?</AlertDialogTitle>
            <AlertDialogDescription>{pick && effect(cur, pick)} Past payments and invoices are not affected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="account-type-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={save} disabled={busy} data-testid="account-type-confirm">{busy ? "Saving..." : "Confirm change"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
